import { useCallback, useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { type ButtonVariant } from '@hivebrite/design-system/Theme';

import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';

interface ConfirmationModalProps {
  cancelButtonLabel?: string;
  children?: React.ReactNode;
  confirmButtonDisabled?: boolean;
  confirmButtonLabel?: string;
  confirmButtonVariant?: ButtonVariant;
  isLoading?: boolean;
  onCancel?: () => void;
  onCloseModal: () => void;
  onConfirmation: () => void;
  text?: string;
  title?: string;
  width?: string;
}

const ConfirmationModal = ({
  cancelButtonLabel = __('Cancel'),
  children,
  confirmButtonLabel = __('Confirm'),
  confirmButtonDisabled = false,
  confirmButtonVariant = 'primary',
  isLoading = false,
  onCloseModal,
  onCancel,
  onConfirmation,
  text,
  title = __('Are you sure?'),
  width = undefined,
}: ConfirmationModalProps) => {
  const [isOpen, setIsOpen] = useState(true);

  const handleCloseModal = useCallback(() => {
    setIsOpen(false);
    onCloseModal();
  }, [onCloseModal]);

  return (
    <ModalWithTemplate
      dataTestId="confirmation-modal"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onCancel || onCloseModal}
            dataTestId="confirmation-modal-cancel"
          >
            {cancelButtonLabel}
          </Button>
          <Button
            dataTestId="confirmation-modal-confirm"
            isDisabled={confirmButtonDisabled}
            isLoading={isLoading}
            ml={4}
            onClick={onConfirmation}
            variant={confirmButtonVariant}
          >
            {confirmButtonLabel}
          </Button>
        </>
      }
      header={title}
      isOpen={isOpen}
      isSticky
      onCloseModal={handleCloseModal}
      screenReadersCloseButtonText={s__(
        'ConfirmationModalAccessibility|Close confirmation window',
      )}
      width={width}
    >
      <Box width="100%" flexDirection="column">
        <Box>{text || children}</Box>
      </Box>
    </ModalWithTemplate>
  );
};

export default ConfirmationModal;
